import { Injectable } from '@angular/core';
import { PropertyDetailsSnapshotCapture } from '@root/shared-report-platform/models/property-details-snapshot-capture.model';
import { PropertyDetailsSnapshotEnvelope } from '@root/shared-report-platform/models/report-snapshot-envelope.model';
import { SharedReportArtifactService } from '@root/shared-report-platform/services/shared-report-artifact.service';
import { Row, Section } from '@store/reports/reports.model';
import { TemplateCode } from '@store/user/user.model';
import { defer, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

interface ImageReference {
  source: string;
  path: string;
  main: boolean;
  replace: (key: string) => void;
}

@Injectable({ providedIn: 'root' })
export class PropertyDetailsSnapshotService {
  constructor(private readonly artifacts: SharedReportArtifactService) {}

  prepareSnapshot(context: PropertyDetailsSnapshotCapture): Observable<PropertyDetailsSnapshotEnvelope> {
    return defer(() => {
      const capturedAt = new Date().toISOString();
      if (!context?.propertyInformation?.propertyIdentifier) {
        throw new Error('A property identifier is required to capture Property Details.');
      }
      const report = context.report;
      if (!report?.isLoaded || report.hasError || report.paymentRequired) {
        throw new Error('Property Details must be loaded without an error or required purchase.');
      }
      const includeAi = !context.aiSummaryLoading && !context.aiSummaryError;
      const captured: PropertyDetailsSnapshotEnvelope = {
        reportId: 'property-details',
        schemaVersion: '1',
        capturedAt,
        renderContext: context.renderContext,
        data: {
          report,
          propertyData: { MLS_PHOTO_IND: context.propertyInformation.propertyData?.MLS_PHOTO_IND }
        },
        artifacts: {}
      };
      // One JSON round trip detaches the entire payload and normalizes optional values.
      const envelope: PropertyDetailsSnapshotEnvelope = JSON.parse(JSON.stringify(captured));
      delete envelope.data.report.indicators;
      if (envelope.data.report.sections) {
        envelope.data.report.sections = this.prepareSections(envelope.data.report.sections, includeAi);
      }

      const references: ImageReference[] = [];
      this.collectImages(envelope.data.report.sections ?? [], 'sections', references);
      const keys = new Map<string, string>();
      const main = references.find(reference => reference.main);
      // Reserve before processing other fields, which may reference the same main photo.
      if (main) {
        keys.set(main.source, 'propertyDetailImage');
      }
      references.forEach(reference => {
        const key = keys.get(reference.source) ?? reference.path;
        keys.set(reference.source, key);
        reference.replace(key);
      });
      if (!keys.size) {
        return of(envelope);
      }
      return forkJoin(Array.from(keys, ([source, key]) =>
        this.artifacts.convertImageUrlToDataUri(source).pipe(
          map(dataUri => [key, dataUri] as const)
        )
      )).pipe(map(entries => {
        envelope.artifacts = Object.fromEntries(entries);
        return envelope;
      }));
    });
  }

  private prepareSections(sections: Section[], includeAi: boolean): Section[] {
    return sections.filter(section => {
      if (section.templateCode === TemplateCode.TMPL_REPORTS_PROPERTY_DETAIL_AI_SUMMARY) {
        const text = section.summary?.[0]?.row?.[0]?.value;
        if (!includeAi || !text?.trim() || text === 'Summary is being generated, please wait...') {
          return false;
        }
        section.summary[0].row.splice(1, 2);
      }
      if (section.sections) {
        section.sections = this.prepareSections(section.sections, includeAi);
      }
      section.groups?.forEach(group => {
        group.sections = this.prepareSections(group.sections, includeAi);
      });
      return true;
    });
  }

  private collectImages(sections: Section[], path: string, references: ImageReference[]): void {
    sections.forEach((section, index) => {
      const prefix = `${path}.${index}`;
      if (section.propertyDetailImage) {
        references.push({
          source: section.propertyDetailImage, path: `${prefix}.propertyDetailImage`, main: true,
          replace: key => section.propertyDetailImage = key
        });
      }
      if (section.image?.url) {
        const image = section.image;
        references.push({
          source: image.url, path: `${prefix}.image.url`, main: false, replace: key => image.url = key
        });
      }
      // Absent sources are skipped: an empty entry would fail artifact conversion and abort the
      // whole snapshot, and replacing it would leave a key pointing at no artifact.
      section.images?.forEach((image, imageIndex) => {
        if (image?.url) {
          references.push({
            source: image.url, path: `${prefix}.images.${imageIndex}.url`, main: false,
            replace: key => image.url = key
          });
        }
      });
      section.imageUrls?.forEach((source, imageIndex, urls) => {
        if (source) {
          references.push({
            source, path: `${prefix}.imageUrls.${imageIndex}`, main: false,
            replace: key => urls[imageIndex] = key
          });
        }
      });
      this.collectRows(section.summary, `${prefix}.summary`, references);
      this.collectRows(section.multiColumn, `${prefix}.multiColumn`, references);
      section.dataGrid?.forEach((grid, gridIndex) =>
        this.collectRows(grid.rows, `${prefix}.dataGrid.${gridIndex}.rows`, references));
      this.collectImages(section.sections ?? [], `${prefix}.sections`, references);
      section.groups?.forEach((group, groupIndex) =>
        this.collectImages(group.sections, `${prefix}.groups.${groupIndex}.sections`, references));
    });
  }

  private collectRows(rows: Row[] | undefined, path: string, references: ImageReference[]): void {
    rows?.forEach((row, rowIndex) => row.row.forEach((cell, cellIndex) => {
      if (cell.image) {
        references.push({
          source: cell.image, path: `${path}.${rowIndex}.row.${cellIndex}.image`, main: false,
          replace: key => cell.image = key
        });
      }
    }));
  }
}
